/* eslint-disable no-unused-vars */
import RecentArticles from '@blocks/RecentArticles'
import {
  Author,
  Chapters,
  FootNote,
  Hero,
  Like,
  Meta,
} from '@components/article'
import Layout from '@components/Layout'
import Seo from '@components/Seo'
import { queries } from '@data/index'
import { useAppContext } from '@hooks/useAppContext'
import { TextPortable } from '@lib/sanity'
import { client } from '@lib/sanity.client'
import { usePageType } from '@state/store'
import Error from 'next/error'
import { groq } from 'next-sanity'
import { PreviewSuspense } from 'next-sanity/preview'
import { lazy, useEffect } from 'react'

import styles from './article.module.scss'

const PreviewArticle = lazy(() => import('../../preview/Article'))

const query = groq`*[_type == "article" && slug.current == $slug][0]{
  ${queries.articleQuery}
}`

export const getStaticPaths = async () => {
  const paths = await client.fetch(
    groq`*[_type == "article" && defined(slug.current) && !(_id in path("drafts.**"))][]{
      "params": { "slug": slug.current }
    }`
  )

  return { paths, fallback: true }
}

export default function ArticlePage({ preview, data }) {
  // console.log('Article Data: ', data)
  const pageType = usePageType()
  const { globals } = useAppContext()
  const { site } = globals

  const { title, seoContent, image, articleContent, relatedArticles, excerpt } =
    data?.article || {}
  const { seoTitle, seoDescription, seoImage } = seoContent || {}

  // console.log('Preview Mode: ', preview)

  useEffect(() => {
    pageType.setPageType('article')
  }, [pageType])

  if (!preview && !data?.article) {
    return (
      <Error
        title={`"Article Page" is not set in Sanity, or the page data is missing`}
        statusCode="Data Error"
      />
    )
  }

  const articleColour = data?.article?.colour?.vibrant?.background || '#ffbdd5'

  return preview ? (
    <PreviewSuspense fallback={<div>Loading...</div>}>
      <PreviewArticle
        query={query}
        queryParams={data?.queryParams}
        site={site}
      />
    </PreviewSuspense>
  ) : (
    <>
      <Seo
        title={seoTitle ? seoTitle : title}
        description={seoDescription ? seoDescription : null}
        image={seoImage ? seoImage : null}
        schemaData={data?.article}
        site={site}
      />

      <Layout hl={articleColour}>
        <Hero image={image} title={title} />

        <h1 className={styles.title}>{title}</h1>

        <Meta data={data?.article} hAlign={'row'} vAlign={'centre'} />

          <div className={`${styles.intro} ${!excerpt && styles.noIntro}`}>
             {excerpt && (
            <p>{excerpt}</p>
            )}
          </div>

        <div className={styles.articleInner}>
          <Chapters data={data?.article} />

          <div id="article-header" className={styles.header}>
            <Author data={data?.article} />
            <Like data={data?.article} />
          </div>

          <section className={styles.wrapper}>
            {articleContent && (
              <TextPortable value={articleContent} placement="article" />
            )}
          </section>
        </div>

        <div id="article-footer" className={styles.footer}>
          <Author data={data?.article} />
          <Like data={data?.article} />
        </div>

        <FootNote />

        {relatedArticles && (
          <RecentArticles
            title={'Want to read some more?'}
            data={relatedArticles}
            type="recent"
            article={true}
          />
        )}
      </Layout>
    </>
  )
}

export const getStaticProps = async ({ params, preview = false }) => {
  const queryParams = { slug: params?.slug ?? `` }

  if (preview) {
    return { props: { preview, data: { queryParams } } }
  }

  const article = await client.fetch(query, queryParams)

  return {
    props: {
      preview,
      data: {
        article,
        queryParams: {},
      },
    },
  }
}
